import { Icon, spacing, Text } from '@scality/core-ui';
import { Box } from '@scality/core-ui/dist/next';
import type React from 'react';
import { useMemo, useState } from 'react';
import { NavLink, useLocation, useParams } from 'react-router';
import styled from 'styled-components';
import {
  SidebarRowLabel,
  sidebarRowSelectedStyles,
  sidebarRowStyles,
} from '../../components/sidebar/sidebarRow.styles';
import type { DashboardSummary } from '../../gen/openapi';
import { filtersToCarryOver } from '../filters';
import { useDashboardTemplates } from '../hooks/useDashboards';
import { productGroupLabelFor, UNGROUPED_LABEL } from '../products';

const DashboardLink = styled(NavLink)`
  ${sidebarRowStyles}

  /* NavLink sets .active on the matched route — that is this list's selected row. */
  &.active {
    ${sidebarRowSelectedStyles}
  }

  padding-left: ${spacing.r24};
`;

/** Full-width: the whole heading is the target, not a chevron glued onto a row. */
const GroupHeader = styled.button`
  ${sidebarRowStyles}

  background: none;
  border: none;
  width: 100%;
  cursor: pointer;
  font: inherit;
  text-align: left;
`;

/** Rotates rather than swapping glyphs. */
const Chevron = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  transition: transform 0.15s ease;
  transform: rotate(${(props) => (props.$open ? '90deg' : '0deg')});
`;

type ProductGroup = {
  label: string;
  templates: DashboardSummary[];
};

/**
 * Groups follow the backend's list order: each sits where its first dashboard does. Anything
 * unlabelled lands in `UNGROUPED_LABEL` last, so no dashboard becomes unreachable by declaring no product.
 */
const groupByProduct = (templates: DashboardSummary[]): ProductGroup[] => {
  const byLabel = new Map<string, DashboardSummary[]>();
  for (const template of templates) {
    const label = productGroupLabelFor(template.products);
    const existing = byLabel.get(label);
    if (existing) existing.push(template);
    else byLabel.set(label, [template]);
  }
  const ungrouped = byLabel.get(UNGROUPED_LABEL);
  byLabel.delete(UNGROUPED_LABEL);
  if (ungrouped) byLabel.set(UNGROUPED_LABEL, ungrouped);
  return [...byLabel].map(([label, templates]) => ({ label, templates }));
};

/**
 * Dashboard-mode sidebar body: the dashboards, grouped by product, each linking to its route.
 *
 * Each link carries the criteria on screen, marked so the dashboard it opens shows them without
 * rendering them (see `filtersToCarryOver`). Dashboards covering other products get a bare link.
 */
export const DashboardList: React.FC = () => {
  const { data: templates } = useDashboardTemplates();
  const groups = useMemo(() => groupByProduct(templates ?? []), [templates]);
  const { templateId } = useParams<{ templateId: string }>();
  const { search } = useLocation();
  const currentProducts = templates?.find((template) => template.id === templateId)?.products;

  // Open by default: a folded list hides the dashboards from someone still learning them.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});

  if (!templates?.length) {
    return (
      <Box display="flex" padding={spacing.r16} justifyContent="center">
        <Text color="textSecondary">No dashboards available</Text>
      </Box>
    );
  }

  return (
    <Box flexDirection="column" flexGrow={1} overflowY="auto">
      {groups.map((group) => {
        const isOpen = !collapsed[group.label];
        return (
          <Box key={group.label} flexDirection="column">
            <GroupHeader
              type="button"
              aria-expanded={isOpen}
              onClick={() => setCollapsed((prev) => ({ ...prev, [group.label]: isOpen }))}
            >
              <Chevron $open={isOpen}>
                <Icon name="Chevron-right" size="sm" color="textSecondary" />
              </Chevron>
              <SidebarRowLabel>
                <Text variant="Smaller" color="textSecondary">
                  {group.label}
                </Text>
              </SidebarRowLabel>
            </GroupHeader>
            {isOpen && (
              <Box flexDirection="column" role="list" aria-label={group.label}>
                {group.templates.map((template) => (
                  <DashboardLink
                    key={template.id}
                    to={{
                      pathname: template.id,
                      search: filtersToCarryOver({ from: currentProducts, to: template.products, search }),
                    }}
                    role="listitem"
                  >
                    <SidebarRowLabel>{template.name}</SidebarRowLabel>
                  </DashboardLink>
                ))}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};
